/**
 * OpenUI Block
 *
 * Renders streamed OpenUI Lang from assistant messages inside chat.
 *
 * Primary responsibilities:
 * - Parse/render OpenUI Lang with the Losi component library
 * - Execute Query()/Mutation() via Losi toolProvider (no LLM round-trip)
 * - Forward ActionButton follow-ups into the chat composer/send path
 * - One soft surface only — children stay flat (no cards-in-cards)
 *
 * Dependencies:
 * - @openuidev/react-lang Renderer
 * - losiOpenUILibrary
 * - createLosiOpenUIToolProvider
 * - ChatbotUIContext (selected Space / workspace)
 */

'use client'

import { useContext, useEffect, useMemo } from 'react'
import { BuiltinActionType, Renderer } from '@openuidev/react-lang'
import type { ActionEvent } from '@openuidev/react-lang'

import { ensureOpenUIEnterStyles } from '@/components/openui/openui-enter-styles'
import {
  isOpenUIFenceLanguage,
  OPENUI_FENCE_LANGUAGES
} from '@/lib/openui/fence'
import { openUIEnterClass, openUISurfaceClass } from '@/lib/openui/chrome'
import { losiOpenUILibrary } from '@/lib/openui/losi-library'
import { createLosiOpenUIToolProvider } from '@/lib/openui/tool-provider'
import { cn } from '@/lib/utils'
import { ChatbotUIContext } from '@/context/context'

export { isOpenUIFenceLanguage, OPENUI_FENCE_LANGUAGES }

type OpenUIBlockProps = {
  code: string
  isStreaming?: boolean
  onActionPrompt?: (prompt: string) => void
  className?: string
}

function resolveActionPrompt(event: ActionEvent | string): string | null {
  if (typeof event === 'string') {
    const trimmed = event.trim()
    return trimmed || null
  }

  const friendly =
    typeof event.humanFriendlyMessage === 'string'
      ? event.humanFriendlyMessage.trim()
      : ''
  if (friendly) return friendly

  if (
    event.type === BuiltinActionType.ContinueConversation ||
    event.type === 'continue_conversation'
  ) {
    const message = event.params?.message
    if (typeof message === 'string' && message.trim()) return message.trim()
  }

  return null
}

function OpenUIQueryLoader() {
  return (
    <div
      className="h-10 animate-pulse rounded-lg bg-foreground/[0.04]"
      aria-hidden="true"
      data-openui-loading="true"
    />
  )
}

export function OpenUIBlock({
  code,
  isStreaming = false,
  onActionPrompt,
  className
}: OpenUIBlockProps) {
  const { selectedSpace, selectedWorkspace } = useContext(ChatbotUIContext)
  const response = useMemo(() => code.trim(), [code])

  useEffect(() => {
    ensureOpenUIEnterStyles()
  }, [])

  const toolProvider = useMemo(
    () =>
      createLosiOpenUIToolProvider({
        spaceId: selectedSpace?.id ?? null,
        workspaceId: selectedWorkspace?.id ?? null
      }),
    [selectedSpace?.id, selectedWorkspace?.id]
  )

  if (!response) return null

  return (
    <div
      className={cn(openUISurfaceClass, openUIEnterClass, className)}
      data-openui-block="true"
    >
      <Renderer
        response={response}
        library={losiOpenUILibrary}
        isStreaming={isStreaming}
        toolProvider={toolProvider}
        queryLoader={<OpenUIQueryLoader />}
        onAction={(event) => {
          if (event.type === BuiltinActionType.OpenUrl || event.type === 'open_url') {
            const url = event.params?.url
            if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
              window.open(url, '_blank', 'noopener,noreferrer')
            }
            return
          }

          const prompt = resolveActionPrompt(event)
          if (prompt && onActionPrompt) {
            if (event.formState && Object.keys(event.formState).length > 0) {
              const extras = Object.entries(event.formState)
                .filter(([, value]) => value !== undefined && value !== '')
                .map(([key, value]) => `${key}: ${String(value)}`)
                .join(', ')
              onActionPrompt(extras ? `${prompt}\n\n(${extras})` : prompt)
              return
            }
            onActionPrompt(prompt)
          }
        }}
      />
    </div>
  )
}
